import { Checkbox, ListItemText } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { useObserver } from 'mobx-react-lite';
import React, { useContext, useEffect, useState } from 'react';
import LoadingIndicator from '../../common/LoadingIndicator';
import NoResultsText from '../../common/NoResultsText';
import MobxContext from '../../mobx/MobxContext';
import ExecutionsTable from './ExecutionsTable';
import { ExecutionEventType } from './ExecutionStore';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textFieldInput: {
      paddingTop: 12,
      paddingBottom: 12
    },
    toolbar: {
      marginBottom: 16,
      display: 'flex',
      justifyContent: 'space-between'
    },
    filtersContainer: {
      marginLeft: 8,
      display: 'flex'
    },
    selectInput: {
      paddingTop: 12,
      paddingBottom: 12
    },
    accountFilter: { 
      marginRight: 8 
    },
    searchField: { 
      width: 300 
    }
  })
);

interface Props {
  familyId: string;
}

function Executions(props: Props) {
  const { familyId } = props;
  const classes = useStyles({});
  const [executions, setExecutions] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filters, setFilters] = useState({ account: [], region: [] });
  const [search, setSearch] = useState('');
  const [page, setPage] = React.useState(0);

  const { executionStore } = useContext(MobxContext);

  useEffect(() => {
    executionStore.getFamilyExecutions(familyId).then(response => setExecutions(response));
  }, [familyId, executionStore]);

  useEffect(() => {
    return executionStore.addExecutionListener((eventType: ExecutionEventType, data: any) => {
      if (!executions) {
        // Reload executions
        executionStore.getFamilyExecutions(familyId).then(response => {
          setExecutions(response);
        });
      } else {
        if (eventType === ExecutionEventType.ExecutionCreated) {
          if (!executions.some(execution => execution.id === data.id)) {
            setExecutions([...executions, data]);
          }
        } else if (eventType === ExecutionEventType.ExecutionUpdated) {
          const index = executions.findIndex(execution => execution.id === data.id);
          if (index !== -1) {
            const executionsCopy = executions.slice();
            executionsCopy[index] = data;
            setExecutions(executionsCopy);
          }
        }
      }
    });
  }, [executionStore, executions, familyId]);

  useEffect(() => {
    if (executions) {
      const { accounts, regions } = executions.reduce((accumulator, execution) => {
        accumulator.accounts.add(execution.account);
        accumulator.regions.add(execution.region);
        return accumulator;
      }, { accounts: new Set(), regions: new Set() });
      setAccounts(Array.from(accounts));
      setRegions(Array.from(regions));
    }
  }, [executions]);

  function onPageChange(newPage: number) {
    setPage(newPage);
  }

  function onSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearch(event.target.value);
    // Reset page
    setPage(0);
  }

  function onFilterChanged(event: React.ChangeEvent<{ name?: string; value: unknown }>) {
    setFilters({ ...filters, [event.target.name as string]: event.target.value });
    // Reset page
    setPage(0);
  }

  const normalizedSearch = search.toLowerCase();
  const hasFilter = Object.keys(filters).some(key => filters[key].length);

  const filteredWorkflows = executions ? executions.filter(execution => {
    if (normalizedSearch && (!execution.name || !execution.name.toLowerCase().includes(normalizedSearch))) {
      return false;
    }

    if (hasFilter && !Object.keys(filters).every(key => !filters[key].length || filters[key].includes(execution[key]))) {
      return false;
    }

    return true;
  }) : null;

  return useObserver(() => (
    <div>
      <div className={classes.toolbar}>
        <TextField
          id="executions-search"
          className={classes.searchField}
          value={search}
          autoComplete="off"
          margin="none"
          variant="outlined"
          placeholder="Search by execution name"
          inputProps={{ 'aria-label': 'bare', className: classes.textFieldInput }}
          onChange={onSearchChange}
        />
        <div className={classes.filtersContainer}>
          <Select
            value={filters.account}
            className={classes.accountFilter}
            displayEmpty
            onChange={onFilterChanged}
            renderValue={() => 'Account'}
            multiple
            input={<OutlinedInput
              labelWidth={0}
              name="account"
              classes={{
                input: classes.selectInput
              }}
            />}
          >
            {accounts.map(account => <MenuItem key={account} value={account}>
              <Checkbox checked={filters.account.includes(account)} color="primary" />
              <ListItemText primary={account} />
            </MenuItem>)}
          </Select>
          <Select
            value={filters.region}
            displayEmpty
            onChange={onFilterChanged}
            renderValue={() => 'Region'}
            multiple
            input={<OutlinedInput
              labelWidth={0}
              name="region"
              classes={{
                input: classes.selectInput
              }}
            />}
          >
            {regions.map(region => <MenuItem key={region} value={region}>
              <Checkbox checked={filters.region.includes(region)} color="primary" />
              <ListItemText primary={region} />
            </MenuItem>)}
          </Select>
        </div>
      </div>
      {filteredWorkflows && filteredWorkflows.length > 0 && <Paper>
        <ExecutionsTable
          familyId={familyId}
          executions={filteredWorkflows}
          page={page}
          onPageChange={onPageChange}
        />
      </Paper>}
      {filteredWorkflows && filteredWorkflows.length === 0 && !hasFilter && <NoResultsText>No executions</NoResultsText>}
      {filteredWorkflows && filteredWorkflows.length === 0 && hasFilter && <NoResultsText>No executions matching filters</NoResultsText>}
      <LoadingIndicator loading={!filteredWorkflows} />
    </div>
  ));
};

export default Executions;
